import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { Alert, AlertDescription } from "@nosible/shared/components/ui/alert";
import { trpc } from "@/admin/lib/trpc";
import { formatDuration, formatTimeAgo, formatTimeUntil } from "@/admin/lib/utils";
import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  Database,
  Clock,
} from "lucide-react";

interface UsageSyncCardProps {
  hasPermission: boolean;
}

export function UsageSyncCard({ hasPermission }: UsageSyncCardProps) {
  const utils = trpc.useUtils();

  const { data: syncStatus } = trpc.admin.sync.usageSync.getSyncStatus.useQuery(
    undefined,
    {
      enabled: hasPermission,
      refetchInterval: 5000,
    }
  );

  const triggerSync = trpc.admin.sync.usageSync.triggerSync.useMutation({
    onSuccess: () => {
      utils.admin.sync.usageSync.getSyncStatus.invalidate();
    },
  });

  return (
    <Card className="bg-slate-core border-chromium-mist/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Usage Sync
            </CardTitle>
            <CardDescription>Redis → Postgres (Usage Data)</CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() => triggerSync.mutate()}
            disabled={syncStatus?.syncInProgress || triggerSync.isPending}
            className="bg-neon-green hover:bg-neon-green/90 text-obsidian"
          >
            {syncStatus?.syncInProgress || triggerSync.isPending ? (
              <>
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-3 w-3" />
                Sync Now
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-chromium-mist">Last Sync</span>
            <div className="flex items-center gap-2">
              {syncStatus?.lastSyncStatus === "success" ? (
                <CheckCircle2 className="h-4 w-4 text-neon-green" />
              ) : syncStatus?.lastSyncStatus === "error" ? (
                <XCircle className="h-4 w-4 text-red-500" />
              ) : null}
              <span className="text-sm font-medium">
                {formatTimeAgo(syncStatus?.lastSyncTime || null)}
              </span>
            </div>
          </div>

          {syncStatus?.lastSyncDuration && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-chromium-mist">Duration</span>
              <span className="text-sm font-medium">
                {formatDuration(syncStatus.lastSyncDuration)}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm text-chromium-mist">Next Sync</span>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-chromium-mist" />
              <span className="text-sm font-medium">
                {formatTimeUntil(syncStatus?.nextSyncTime || null)}
              </span>
            </div>
          </div>
        </div>

        {syncStatus?.stats?.lastKeysSynced !== null && syncStatus?.stats?.lastSubsSynced !== null && (
          <div className="space-y-3">
            <div className="text-sm font-medium text-ghost-white">Last Sync</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-obsidian/50 p-2 rounded text-center">
                <div className="text-chromium-mist">Keys Synced</div>
                <div className="font-bold text-base">{syncStatus?.stats?.lastKeysSynced ?? 0}</div>
              </div>
              <div className="bg-obsidian/50 p-2 rounded text-center">
                <div className="text-chromium-mist">Subs Synced</div>
                <div className="font-bold text-base">{syncStatus?.stats?.lastSubsSynced ?? 0}</div>
              </div>
            </div>
          </div>
        )}

        {syncStatus?.lastSyncError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              {syncStatus.lastSyncError}
            </AlertDescription>
          </Alert>
        )}

        {triggerSync.isSuccess && (
          <Alert className="bg-neon-green/10 border-neon-green">
            <CheckCircle2 className="h-4 w-4 text-neon-green" />
            <AlertDescription className="text-xs text-neon-green">
              {triggerSync.data.message}
            </AlertDescription>
          </Alert>
        )}

        <div className="text-xs text-chromium-mist/70 pt-2 border-t border-chromium-mist/10">
          Scheduled every hour. Usage data is synced from Redis to Postgres.
        </div>
      </CardContent>
    </Card>
  );
}
